import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BuildPriority } from '@hangar/shared';
import { cancelBuild, createBuilds, getBuild, getBuildEnv, getQueue, listBuildArtifacts, listBuildChecks, listBuildEvents, listBuilds, retryBuild, setBuildPriority, type BuildCreateInput, type BuildFilters } from './endpoints';
import { type Build } from './schemas';
import { qk } from './queryKeys';

/* ---------- Builds ---------- */

/** Cursor-paginated builds list ("Load more"); the SSE hook refreshes it on every `build.*` event. */
export const useBuilds = (filters: BuildFilters, enabled = true) =>
  useInfiniteQuery({
    queryKey: qk.builds(filters),
    queryFn: ({ pageParam }) => listBuilds(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });

export const useBuild = (buildId: string | undefined) =>
  useQuery({ queryKey: qk.build(buildId ?? ''), queryFn: () => getBuild(buildId ?? ''), enabled: Boolean(buildId) });
export const useBuildEvents = (buildId: string | undefined) =>
  useQuery({ queryKey: qk.buildEvents(buildId ?? ''), queryFn: async () => (await listBuildEvents(buildId ?? '')).data, enabled: Boolean(buildId) });
export const useBuildChecks = (buildId: string | undefined) =>
  useQuery({ queryKey: qk.buildChecks(buildId ?? ''), queryFn: async () => (await listBuildChecks(buildId ?? '')).data, enabled: Boolean(buildId) });
export const useBuildArtifacts = (buildId: string | undefined) =>
  useQuery({ queryKey: qk.buildArtifacts(buildId ?? ''), queryFn: async () => (await listBuildArtifacts(buildId ?? '')).data, enabled: Boolean(buildId) });
export const useBuildEnv = (buildId: string | undefined, enabled = true) =>
  useQuery({ queryKey: qk.buildEnv(buildId ?? ''), queryFn: () => getBuildEnv(buildId ?? ''), enabled: Boolean(buildId) && enabled });
export const useQueue = (projectId?: string, enabled = true) => useQuery({ queryKey: qk.queue(projectId), queryFn: () => getQueue(projectId), enabled, staleTime: 10_000 });

/** Writes the build into every cache that holds it (detail + list pages). */
export function useBuildMutations(buildId: string) {
  const qc = useQueryClient();
  const settle = (build: Build) => {
    qc.setQueryData(qk.build(build.id), build);
    void qc.invalidateQueries({ queryKey: ['builds'] });
    void qc.invalidateQueries({ queryKey: qk.buildEvents(build.id) });
  };
  const cancel = useMutation({ mutationFn: (reason?: string) => cancelBuild(buildId, reason), onSuccess: settle });
  const retry = useMutation({ mutationFn: (idempotencyKey: string) => retryBuild(buildId, idempotencyKey), onSuccess: (b) => void qc.invalidateQueries({ queryKey: ['builds'] }).then(() => settle(b)) });
  const priority = useMutation({ mutationFn: (v: { priority: BuildPriority; reason?: string }) => setBuildPriority(buildId, v.priority, v.reason), onSuccess: settle });
  return { cancel, retry, priority };
}

export function useCreateBuilds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { body: BuildCreateInput; idempotencyKey?: string }) => createBuilds(v.body, v.idempotencyKey),
    onSuccess: (result) => {
      if (result.dryRun) return;
      for (const b of result.builds) qc.setQueryData(qk.build(b.id), b);
      void qc.invalidateQueries({ queryKey: ['builds'] });
      void qc.invalidateQueries({ queryKey: ['queue'] });
    },
  });
}
